import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getProjectsSimpleTableIndicator } from '../../../redux/actions/indicatorProjects';
import { handleLoading } from '../../utils';
import { FilterTabs } from './FilterTabs';
import { Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import styles from './FinancialPlansSoonToExperieAndExpired.module.scss';
import { FinancialPlansSoonToExperieAndExpiredTable } from './Table/FinancialPlansSoonToExperieAndExpiredTable';
import useTable from '../../../hooks/useTable';
import FilterIndicators from '../../../models/FilterIndicators';
import { AppMenuTabs } from '../../MenuTabs/userTabsInterface';
import { setIndicatorResponse, setLoading } from '../../../redux/actions/dataTable';
import { getNewFilteredIndicators } from '../../../api/indicatorApi';
import { translateMultipleText } from '../../../services/stringUtil';
import { emptyProjects } from '../../Portfolio/PortfolioUtil';
import { processIndicators } from '../../Portfolio/indicatorHelper';
import { ProjectProductTypes } from '../../../models/ProjectProductTypes';
import { arrayMove } from 'react-sortable-hoc';
import { addIndicators } from '../../../redux/actions/indicators';
import { setCurrentIndicator } from '../../../redux/actions/currentIndicator';
import { updateCreatingOrEditing } from '../../../redux/actions/menuTabs';
import { resetIndicatorDetailFilter } from '../../../redux/actions/indicatorDetailFilters';
import FiltersErrorMessage from '../../Util/Errors/FiltersErrorMessage/FiltersErrorMessage';

export const FinancialPlansSoonToExperieAndExpired = () => {
  const dispatch = useDispatch();
  const { projects } = useTable();
  const [dataFilter, setDataFilter] = useState<any>(projects);
  const { currentIndicator } = useSelector<any, any>(state => state);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const { t } = useTranslation();

  const {
    plansExpiredCount,
    planNextExpiredCount,
    unexpiredPlansCount,
    amountExpiredCount,
    amountNextExpiredCount,
    amountUnexpiredPlansCount,
  } = currentIndicator.boxValues ?? {
    plansExpiredCount: 0,
    planNextExpiredCount: 0,
    unexpiredPlansCount: 0,
    amountExpiredCount: 0,
    amountNextExpiredCount: 0,
    amountUnexpiredPlansCount: 0,
  };

  const dataStateButtons = [
    {
      id: 2,
      filterIcon: 2,
      filterLabelRight: 'expired',
      filterLabelLeft: 'new_total_amount_to_justify',
      projectLabel: { singular: 'new_anticipation', plural: 'anticipations' },
      projectAmountLabel: { singular: 'M', plural: 'M' },
      projectQuantity: plansExpiredCount,
      projectAmount: amountExpiredCount,
      selected: false,
    },
    {
      id: 1,
      filterIcon: 1,
      filterLabelRight: 'new_due_in_the_next_30_days',
      filterLabelLeft: 'new_total_amount_to_justify',
      projectLabel: { singular: 'new_anticipation', plural: 'anticipations' },
      projectAmountLabel: { singular: 'M', plural: 'M' },
      projectQuantity: planNextExpiredCount,
      projectAmount: amountNextExpiredCount,
      selected: false,
    },
    {
      id: 0,
      filterIcon: 0,
      filterLabelRight: 'new_within_the_deadline',
      filterLabelLeft: 'new_total_amount_to_justify',
      projectLabel: { singular: 'new_anticipation', plural: 'anticipations' },
      projectAmountLabel: { singular: 'M', plural: 'M' },
      projectQuantity: unexpiredPlansCount,
      projectAmount: amountUnexpiredPlansCount,
      selected: false,
    },
  ];

  const [dataButtonsFilteState, setDataButtonsFilterState] = useState(dataStateButtons);
  const [selectLabel, setSelectLabel] = useState<string>();

  const { plansExpiredPercent, planNextExpiredPercent, unexpiredPlansPercent } = currentIndicator.percentValue ?? {
    plansExpiredPercent: 0,
    planNextExpiredPercent: 0,
    unexpiredPlansPercent: 0,
  };

  // Filter
  const filterIndicators = useSelector<any, FilterIndicators>(state => state.filterIndicators);
  const stateProjectProductTypes = useSelector<any, FilterIndicators>(
    state => state.indicatorPayload.payload.projectProductTypes,
  );
  const menuTabs = useSelector<any, AppMenuTabs>(state => state.menuTabs);
  const portfolioSelectedTab = menuTabs.portfolioSelectedTab;
  const creatingOrEditing = menuTabs.creatingOrEditing;
  const [isLoading, setIsLoading] = useState(false);
  const [totalProjectsData, setTotalProjectsData] = useState(false);
  const [flag, setFlag] = useState<boolean>(false);
  const [hasError, setHasError] = useState(false);

  const projectTypeFilterChange = {
    ...filterIndicators,
    common: { ...filterIndicators.common, projectTypeFilters: stateProjectProductTypes },
    projectTypeFilters: stateProjectProductTypes,
  };

  const handleCommonFilterChange = async () => {
    dispatch(setLoading(true));
    setIsLoading(true);
    setHasError(false);

    try {
      const indicatorsToProcess = await getNewFilteredIndicators(
        {},
        { projectProductTypes: stateProjectProductTypes },
        {
          ...projectTypeFilterChange.common,
        },
      );
      dispatch(setIndicatorResponse(indicatorsToProcess));
      startProcessIndicators(
        indicatorsToProcess,
        translateMultipleText(` ${' '} ${portfolioSelectedTab.title}`, t),
        filterIndicators.common.projectTypeFilters,
      );
      setTotalProjectsData(emptyProjects(indicatorsToProcess.totalProjects));
    } catch (err) {
      setHasError(true);
      console.error(err, 'Error fetching data from indicators');
    } finally {
      dispatch(setLoading(false));
      setIsLoading(false);
    }
  };

  const startProcessIndicators = (inds, titleFromTab, projectTypeFilter = [ProjectProductTypes.Loan]) => {
    const result = processIndicators(inds, {
      titleFromTab,
      projectTypeFilter,
    });
    const elementSummary: any = result.find(elem => elem.id === 15);
    const indexSummary: number = result.indexOf(elementSummary);
    const sendEndSummary = arrayMove(result, indexSummary, result.length);

    dispatch(addIndicators(sendEndSummary));
    dispatch(
      setCurrentIndicator(sendEndSummary?.find(data => data.indicatorKey === 'FINANCIAL_PLANS_ABOUT_EXPIRE_EXPIRED')),
    );
    setIsLoading(false);
  };

  // const tabId0OrCreatingEditing = (menuTabs, creatingOrEditing) =>
  //   menuTabs.portfolioSelectedTab.tabId === 0 || creatingOrEditing;

  useEffect(() => {
    getFilteredSimpleIndicators(
      dispatch,
      currentIndicator.id,
      currentIndicator.indicatorKey,
      setLoadingData,
      // projectTypeFilterChange,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndicator.id, dispatch, filterIndicators.common]);

  useEffect(() => {
    if (
      filterIndicators.common.coReponsibleDivisions.length > 0 ||
      filterIndicators.common.countryId.length > 0 ||
      filterIndicators.common.divisionId.length > 0 ||
      filterIndicators.common.interestIssues.length > 0 ||
      filterIndicators.common.participantDivisions.length > 0 ||
      filterIndicators.common.regionId.length > 0 ||
      filterIndicators.common.sectors.length > 0 ||
      filterIndicators.common.teamLeaders.length > 0
    )
      handleCommonFilterChange();
    setFlag(true);
    // setDataButtonsFilterState(dataStateButtons);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common]);

  useEffect(() => {
    dispatch(resetIndicatorDetailFilter());
    if (
      !menuTabs.firstLoad &&
      // tabId0OrCreatingEditing(menuTabs, creatingOrEditing) &&
      filterIndicators.common.coReponsibleDivisions.length === 0 &&
      filterIndicators.common.countryId.length === 0 &&
      filterIndicators.common.divisionId.length === 0 &&
      filterIndicators.common.interestIssues.length === 0 &&
      filterIndicators.common.participantDivisions.length === 0 &&
      filterIndicators.common.regionId.length === 0 &&
      filterIndicators.common.sectors.length === 0 &&
      filterIndicators.common.teamLeaders.length === 0 &&
      flag
    ) {
      handleCommonFilterChange();
      setFlag(false);
      // setDataButtonsFilterState(dataStateButtons);
      if (creatingOrEditing) {
        dispatch(updateCreatingOrEditing(false));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common]);

  useEffect(() => {
    if (projects) {
      setDataFilter(projects);
    }
  }, [projects]);

  useEffect(() => {
    if (!isLoading) setDataButtonsFilterState(dataStateButtons);
  }, [isLoading]);

  const dimensionsBar = { height: 200 };
  const hasDoughnutData = [plansExpiredCount, planNextExpiredCount, unexpiredPlansCount].some(value => Number(value) > 0);
  const doughnutValues = hasDoughnutData ? [plansExpiredPercent, planNextExpiredPercent, unexpiredPlansPercent] : [1];
  const doughnutColors = hasDoughnutData ? ['#F06D6D', '#F7DD00', '#91E2A1'] : ['#D0D5E0'];

  const data = {
    datasets: [
      {
        label: t('new_orginila_projection'),
        data: doughnutValues,
        backgroundColor: doughnutColors,
        dataLabelsTranslations: ['expired', 'qualified_opinion', 'adverse_opinion'],
        barThickness: 10,
        hoverBackgroundColor: doughnutColors,
        projectQuantity: hasDoughnutData ? [plansExpiredCount, planNextExpiredCount, unexpiredPlansCount] : [0],
      },
    ],
    labels: hasDoughnutData ? ['expired', 'new_due_in_the_next_30_days', 'new_within_the_deadline'] : [''],
    amountData: hasDoughnutData ? [amountExpiredCount, amountNextExpiredCount, amountUnexpiredPlansCount] : [0],
  };

  const options = {
    cutoutPercentage: 75,
    tooltips: {
      enabled: hasDoughnutData,
      backgroundColor: '#101319',
      bodyFontColor: '#ffffff',
      bodyFontFamily: 'Rubik',
      titleFontColor: '#ffffff',
      titleFontSize: 14,
      bodyFontSize: 14,
      callbacks: {
        title: (tooltipItem, data) => {
          return t(data['labels'][tooltipItem[0]['index']]);
        },
        label: (tooltipItem, data) => {
          return `${data['datasets'][0]['data'][tooltipItem['index']].toFixed(1)} % • ${data['datasets'][0][
            'projectQuantity'
          ][tooltipItem['index']].toFixed(0)} ${data['datasets'][0]['projectQuantity'][tooltipItem['index']].toFixed(0) > 1
            ? t('anticipations')
            : t('new_anticipation')
            } • ${data['amountData'][tooltipItem.index].toLocaleString()} M`;
        },
      },
    },
    legend: {
      display: false,
    },
    plugins: {
      datalabels: { display: false },
    },
    animation: {
      duration: 1000,
    },
  };

  return (
    <>
      <section className="container-filter-tabs">
        <FilterTabs
          dataTabs={dataButtonsFilteState}
          fnDataFilter={setDataFilter}
          dataFilter={projects}
          fnDataButtonsFilter={setDataButtonsFilterState}
          fnSelectLabel={setSelectLabel}
        />
      </section>
      <section className={styles['container-granphic']}>
        <Doughnut data={data} options={options} {...dimensionsBar} />
      </section>
      <section className={styles['container-table']}>
        {hasError ? (
          <div className={styles.emptyRowContent}>
            <FiltersErrorMessage isTable={true} />
          </div>
        ) : (
          <FinancialPlansSoonToExperieAndExpiredTable
            data={dataFilter}
            fnDataFilter={setDataFilter}
            dataTabs={dataButtonsFilteState}
            fnDataButtonsFilter={setDataButtonsFilterState}
            selectLabel={selectLabel}
            fnSelectLabel={setSelectLabel}
            isLoading={isLoading}
          />
        )}
      </section>
    </>
  );
};

const getFilteredSimpleIndicators = (
  d,
  currentIndicatorId,
  currentIndicatorKey,
  setLoading,
  projectTypeFilterChange?,
) => {
  return handleLoading(
    () => d(getProjectsSimpleTableIndicator(currentIndicatorId, currentIndicatorKey, projectTypeFilterChange)),
    setLoading,
  );
};
